import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import API from "../../../api/api";
import SearchableSelect from "../../../components/common/SearchableSelect";
import { toastMessageAlert } from "../../../utils/alert";

const normalizeType = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s*\/\s*/g, "_")
    .replace(/\s+/g, "_")
    .trim();

const isTimedLike = (row) => {
  const type = normalizeType(row?.game_type || row?.competition_type || "");
  if (type === "TIMED") return true;
  const sport = String(row?.sport_name || "").toLowerCase();
  const game = String(row?.game_name || "").toLowerCase();
  const narr = String(row?.game_narration || "").toLowerCase();
  if (sport.includes("swim")) return true;
  return /(freestyle|backstroke|breaststroke|butterfly|medley|relay|sprint|run|race|time trial|\b\d+m\b)/.test(
    `${game} ${narr}`
  );
};
const isParaAthleticsSport = (sportName) =>
  String(sportName || "")
    .toLowerCase()
    .includes("athlet");
const formatTimeMask = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 7);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 7)}`;
};
const isFullMaskedTime = (value) => /^\d{2}:\d{2}:\d{1,3}$/.test(String(value || "").trim());
const rowKey = (row) => `${row.registration_id}_${row.game_id || 0}`;
const toNonNegativeMs = (value) => Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0);
const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");
const timeMaskToMs = (value) => {
  const match = String(value || "").trim().match(/^(\d{2}):(\d{2}):(\d{1,3})$/);
  if (!match) return 0;
  const mm = Number(match[1]);
  const ss = Number(match[2]);
  const mmm = Number(String(match[3]).padEnd(3, "0"));
  if (!Number.isFinite(mm) || !Number.isFinite(ss) || !Number.isFinite(mmm)) return 0;
  return mm * 60000 + ss * 1000 + mmm;
};
const msToTimeMask = (value) => {
  const ms = toNonNegativeMs(value);
  const mm = Math.floor(ms / 60000);
  const ss = Math.floor((ms % 60000) / 1000);
  const mmm = Math.floor(ms % 1000);
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}:${String(mmm).padStart(3, "0")}`;
};
const normalizeGenderToken = (value) => {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "male" || raw === "men" || raw === "m") return "male";
  if (raw === "female" || raw === "women" || raw === "f") return "female";
  return "all";
};
const toUniqueClassificationIds = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
    )
  );
const generatedSheetClassificationText = (row) => {
  const names = (Array.isArray(row?.classification_names) ? row.classification_names : [])
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  const ids = toUniqueClassificationIds(row?.classification_ids);
  const includeUnclassified = Boolean(row?.include_unclassified);
  const base = names.length
    ? names.join(", ")
    : (ids.length ? ids.map((id) => `Class ${id}`).join(", ") : "All classifications");
  return includeUnclassified ? `${base} + Missed / New Player` : base;
};
const pickGeneratedSheetForResults = (rows, games, activeGender) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const availableGameIds = new Set(
    (Array.isArray(games) ? games : [])
      .map((g) => String(g?.id || "").trim())
      .filter(Boolean)
  );
  const filteredByGame = list.filter((row) => {
    const gameId = Number(row?.game_id || 0);
    if (gameId <= 0) return true;
    if (!availableGameIds.size) return false;
    return availableGameIds.has(String(gameId));
  });
  if (!filteredByGame.length) return null;
  const wantedGender = normalizeGenderToken(activeGender);
  if (wantedGender === "all") {
    return filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") || filteredByGame[0];
  }
  return (
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === wantedGender) ||
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") ||
    filteredByGame[0]
  );
};
const formatGeneratedUpdatedAt = (value) => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const TimedResults = () => {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGender = useMemo(() => {
    const g = (searchParams.get("gender") || "all").toLowerCase();
    return ["all", "male", "female"].includes(g) ? g : "all";
  }, [searchParams]);
  const [gender, setGender] = useState(initialGender);
  const date = searchParams.get("date") || "";
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (event_id) q.set("event_id", String(event_id));
    q.set("gender", gender || "all");
    return q.toString();
  }, [date, event_id, gender]);
  const activeGenderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "All";
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [eventMeta, setEventMeta] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedClassificationIds, setSelectedClassificationIds] = useState([]);
  const [mixClassificationsForCompute, setMixClassificationsForCompute] = useState(false);
  const [editCombinedSelection, setEditCombinedSelection] = useState(false);
  const [appliedGeneratedSheet, setAppliedGeneratedSheet] = useState(null);
  const [message, setMessage] = useState("");
  const [exportingKey, setExportingKey] = useState("");
  const [sortKey, setSortKey] = useState("game_classification");
  const [sortDir, setSortDir] = useState("asc");
  const [gameMode, setGameMode] = useState("manual");
  const [selectedCompetitorKey, setSelectedCompetitorKey] = useState("");
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerStartRef = useRef(0);
  const timerBaseMsRef = useRef(0);
  const timerTickerRef = useRef(null);

  useEffect(() => {
    if (initialGender !== gender) {
      setGender(initialGender);
    }
  }, [initialGender, gender]);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  useEffect(() => {
    setSelectedGameId("");
    setSelectedClassificationIds([]);
    setMixClassificationsForCompute(false);
    setEditCombinedSelection(false);
    setAppliedGeneratedSheet(null);
  }, [event_id]);

  useEffect(() => {
    let alive = true;
    const loadPageData = async () => {
      if (!event_id) return;

      const loadPlayers = async () => {
        try {
          const timedRes = await API.fetchEventPlayers(event_id, {
            game_types: "TIMED",
            gender: gender === "all" ? undefined : gender,
          });
          const timedRows = timedRes?.data || timedRes || [];
          const timedList = Array.isArray(timedRows) ? timedRows : [];
          if (timedList.length) return timedList;

          const allRes = await API.fetchEventPlayers(event_id, {
            gender: gender === "all" ? undefined : gender,
          });
          const allRows = allRes?.data || allRes || [];
          const allList = Array.isArray(allRows) ? allRows : [];
          return allList.filter((row) => isTimedLike(row));
        } catch (_error) {
          return [];
        }
      };

      try {
        const [contextResult, generatedResult, playersResult] = await Promise.allSettled([
          API.fetchResultEventContext(event_id),
          API.fetchGeneratedScoreSheets({ event_id: Number(event_id) }),
          loadPlayers(),
        ]);

        if (!alive) return;

        const ctx =
          contextResult.status === "fulfilled"
            ? (contextResult.value?.data || contextResult.value || {})
            : {};
        const gameList = Array.isArray(ctx?.games) ? ctx.games : [];
        const timedGames = gameList.filter((game) => isTimedLike(game));
        const scopedGames = timedGames.length ? timedGames : gameList;
        setEventMeta(ctx?.event || null);
        setGames(scopedGames);
        const loadedPlayers = playersResult.status === "fulfilled" ? playersResult.value : [];
        setPlayers(Array.isArray(loadedPlayers) ? loadedPlayers : []);

        const rows = generatedResult.status === "fulfilled" && Array.isArray(generatedResult.value)
          ? generatedResult.value
          : [];
        const picked = pickGeneratedSheetForResults(rows, scopedGames, gender);
        if (!picked) {
          setSelectedGameId("");
          setSelectedClassificationIds([]);
          setMixClassificationsForCompute(false);
          setEditCombinedSelection(false);
          setAppliedGeneratedSheet(null);
          return;
        }

        const generatedClassIds = toUniqueClassificationIds(picked?.classification_ids);
        const scopedGameIds = new Set(
          scopedGames.map((game) => String(game?.id || "").trim()).filter(Boolean)
        );
        const preferredGameId =
          picked?.game_id && scopedGameIds.has(String(picked.game_id)) ? String(picked.game_id) : "";
        const isCombinedMode = String(picked?.mode || "").toLowerCase() === "combined";
        const mixedPossible = isCombinedMode && generatedClassIds.length >= 2;
        const selectedGameName =
          scopedGames.find((game) => String(game.id) === String(preferredGameId))?.game_name ||
          (preferredGameId ? `Game ${preferredGameId}` : "All Games");

        setSelectedGameId(preferredGameId);
        setSelectedClassificationIds(generatedClassIds);
        setMixClassificationsForCompute(mixedPossible);
        setEditCombinedSelection(false);
        setAppliedGeneratedSheet({
          id: picked?.id ?? null,
          mode: mixedPossible ? "Combined Categories" : "Individual Categories",
          gameName: selectedGameName,
          categoryText: generatedSheetClassificationText(picked),
          totalPlayers: Number.isFinite(Number(picked?.total_players)) ? Number(picked.total_players) : null,
          updatedAt: formatGeneratedUpdatedAt(picked?.updated_at || picked?.created_at),
        });
      } catch (_error) {
        if (!alive) return;
        setEventMeta(null);
        setGames([]);
        setPlayers([]);
        setAppliedGeneratedSheet(null);
      }
    };

    loadPageData();
    return () => {
      alive = false;
    };
  }, [event_id, gender]);

  const filteredPlayers = players.filter((p) => {
    if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
    if (selectedClassificationIds.length) {
      const cid = String(p.classification_id || "");
      if (!selectedClassificationIds.includes(cid)) return false;
    }
    return true;
  });

  const sortedPlayers = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const list = [...filteredPlayers];
    return list.sort((a, b) => {
      const compareText = (x, y) => {
        const ax = String(x ?? "").trim();
        const by = String(y ?? "").trim();
        const aBlank = isBlank(ax);
        const bBlank = isBlank(by);
        if (aBlank && bBlank) return 0;
        if (aBlank) return 1;
        if (bBlank) return -1;
        const base = normalizeText(ax).localeCompare(normalizeText(by), undefined, { numeric: true });
        return base * dir;
      };
      const compareNumber = (x, y) => {
        const nx = Number(x);
        const ny = Number(y);
        const xValid = Number.isFinite(nx);
        const yValid = Number.isFinite(ny);
        if (!xValid && !yValid) return 0;
        if (!xValid) return 1;
        if (!yValid) return -1;
        if (nx === ny) return 0;
        const base = nx < ny ? -1 : 1;
        return base * dir;
      };

      const gameCmp = compareText(a.game_name, b.game_name);
      const classCmp = compareText(
        a.classification_name || a.classification,
        b.classification_name || b.classification
      );
      const gameAndClassCmp = gameCmp !== 0 ? gameCmp : classCmp;

      let cmp = 0;
      switch (sortKey) {
        case "game_classification":
          cmp = gameAndClassCmp;
          break;
        case "game":
          cmp = gameCmp !== 0 ? gameCmp : classCmp;
          break;
        case "gender":
          cmp = compareText(a.gender, b.gender);
          break;
        case "district":
          cmp = compareText(a.district, b.district);
          break;
        case "category":
          cmp = compareText(a.age_category, b.age_category);
          break;
        case "classification":
          cmp = classCmp !== 0 ? classCmp : gameCmp;
          break;
        case "time":
          cmp = compareNumber(
            isBlank(a.result_time) ? NaN : timeMaskToMs(a.result_time),
            isBlank(b.result_time) ? NaN : timeMaskToMs(b.result_time)
          );
          break;
        case "points":
          cmp = compareNumber(a.points_score, b.points_score);
          break;
        case "rank":
          cmp = compareNumber(a.result_position, b.result_position);
          break;
        case "name":
        default:
          cmp = compareText(a.name, b.name);
          break;
      }
      if (cmp !== 0) return cmp;
      if (gameAndClassCmp !== 0) return gameAndClassCmp;
      return compareText(a.name, b.name);
    });
  }, [filteredPlayers, sortDir, sortKey]);

  const gameOptions = [
    ...new Map(
      [
        ...games.map((g) => [String(g.id || ""), g.game_name || `Game ${g.id}`]),
        ...players.map((p) => [String(p.game_id || ""), p.game_name || `Game ${p.game_id}`]),
      ]
    ).entries(),
  ].filter(([id]) => id);
  const selectedGame = games.find((g) => String(g.id) === String(selectedGameId)) || null;
  const classificationMap = useMemo(() => {
    const map = new Map();
    players
      .filter((p) => !selectedGameId || String(p.game_id) === String(selectedGameId))
      .forEach((p) => {
        const id = String(p.classification_id || "");
        if (!id) return;
        const name = p.classification_name || `Class ${id}`;
        if (!map.has(id)) {
          map.set(id, { id, name, count: 0 });
        }
        map.get(id).count += 1;
      });
    return map;
  }, [players, selectedGameId]);
  const classificationOptions = useMemo(
    () => Array.from(classificationMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    [classificationMap]
  );
  const combinedCategoryOption = useMemo(() => {
    if (!mixClassificationsForCompute || selectedClassificationIds.length < 2) return null;
    const selectedSet = new Set(selectedClassificationIds.map((id) => String(id)));
    const selectedNames = selectedClassificationIds
      .map((id) => classificationMap.get(String(id))?.name || `Class ${id}`);
    const uniqueNames = Array.from(new Set(selectedNames));
    const combinedLabel = uniqueNames.join(" + ");
    const combinedCount = players.filter((p) => {
      if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
      return selectedSet.has(String(p.classification_id || ""));
    }).length;
    return {
      id: "__combined__",
      name: combinedLabel || "Combined Categories",
      details: combinedLabel || "Combined Categories",
      count: combinedCount,
    };
  }, [mixClassificationsForCompute, selectedClassificationIds, classificationMap, players, selectedGameId]);
  const showCombinedAsSingleOption = Boolean(combinedCategoryOption) && !editCombinedSelection;
  const mixedClassificationIdSet = useMemo(
    () => new Set(selectedClassificationIds.map((id) => String(id))),
    [selectedClassificationIds]
  );
  const selectedCompetitor = useMemo(
    () => filteredPlayers.find((row) => rowKey(row) === selectedCompetitorKey) || null,
    [filteredPlayers, selectedCompetitorKey]
  );
  const getClassificationDisplayName = (row) => {
    if (
      mixClassificationsForCompute &&
      mixedClassificationIdSet.size >= 2 &&
      mixedClassificationIdSet.has(String(row?.classification_id || ""))
    ) {
      return combinedCategoryOption?.name || "Combined Categories";
    }
    return row?.classification_name || "Unclassified";
  };

  useEffect(() => {
    if (!mixClassificationsForCompute || selectedClassificationIds.length < 2) {
      setEditCombinedSelection(false);
    }
  }, [mixClassificationsForCompute, selectedClassificationIds]);

  useEffect(() => {
    if (!filteredPlayers.length) {
      setSelectedCompetitorKey("");
      return;
    }
    const exists = filteredPlayers.some((row) => rowKey(row) === selectedCompetitorKey);
    if (!exists) {
      setSelectedCompetitorKey(rowKey(filteredPlayers[0]));
    }
  }, [filteredPlayers, selectedCompetitorKey]);

  useEffect(() => {
    if (!timerRunning) {
      if (timerTickerRef.current) {
        clearInterval(timerTickerRef.current);
        timerTickerRef.current = null;
      }
      return undefined;
    }

    timerStartRef.current = Date.now();
    timerTickerRef.current = setInterval(() => {
      const delta = Date.now() - timerStartRef.current;
      setLiveElapsedMs(toNonNegativeMs(timerBaseMsRef.current + delta));
    }, 10);

    return () => {
      if (timerTickerRef.current) {
        clearInterval(timerTickerRef.current);
        timerTickerRef.current = null;
      }
    };
  }, [timerRunning]);

  useEffect(
    () => () => {
      if (timerTickerRef.current) {
        clearInterval(timerTickerRef.current);
        timerTickerRef.current = null;
      }
    },
    []
  );

  const updateField = (registrationId, gameId, field, value) => {
    const copy = players.map((row) => {
      if (
        String(row.registration_id) === String(registrationId) &&
        String(row.game_id) === String(gameId)
      ) {
        return { ...row, [field]: field === "result_time" ? formatTimeMask(value) : value };
      }
      return row;
    });
    setPlayers(copy);
  };

  const persistAll = async () => {
    const invalidRows = [];
    const results = filteredPlayers
      .map((p) => {
        const t = formatTimeMask(p.result_time || "").trim();
        if (t !== "" && !isFullMaskedTime(t)) {
          invalidRows.push(p.name || `Reg#${p.registration_id}`);
          return null;
        }
        if (t === "" && (p.result_position === "" || p.result_position === null || p.result_position === undefined)) {
          return null;
        }
        return {
          registration_id: p.registration_id,
          game_id: p.game_id || null,
          result_time: t || null,
          result_position: p.result_position || null,
        };
      })
      .filter(Boolean);

    try {
      if (invalidRows.length) {
        setMessage(`Invalid time format for: ${invalidRows.slice(0, 5).join(", ")}. Use mm:ss:ms (1-3 digits).`);
        return false;
      }
      if (!results.length) {
        setMessage("Enter at least one valid time in mm:ss:ms (1-3 digits) before saving.");
        return false;
      }
      await API.saveTimedResults({ results });
      return true;
    } catch (err) {
      setMessage(err?.message || "Save failed.");
      return false;
    }
  };

  const saveAll = async () => {
    const ok = await persistAll();
    if (!ok) return;
    try {
      const timedRes = await API.fetchEventPlayers(event_id, {
        game_types: "TIMED",
        gender: gender === "all" ? undefined : gender,
      });
      const timedRows = timedRes?.data || timedRes || [];
      const timedList = Array.isArray(timedRows) ? timedRows : [];
      if (timedList.length) {
        setPlayers(timedList);
      } else {
        const allRes = await API.fetchEventPlayers(event_id, {
          gender: gender === "all" ? undefined : gender,
        });
        const allRows = allRes?.data || allRes || [];
        const allList = Array.isArray(allRows) ? allRows : [];
        setPlayers(allList.filter((r) => isTimedLike(r)));
      }
      setMessage("Results saved successfully.");
    } catch (_err) {
      setMessage("Results saved, but refresh failed.");
    }
  };

  const computeResults = async () => {
    try {
      if (!selectedGameId) {
        setMessage("Select a game to compute timed positions.");
        return;
      }
      if (mixClassificationsForCompute && selectedClassificationIds.length < 2) {
        setMessage("Select at least 2 classifications to mix.");
        return;
      }

      const saved = await persistAll();
      if (!saved) return;
      await API.computePositions({
        event_id: Number(event_id),
        game_id: Number(selectedGameId),
        mix_classifications: mixClassificationsForCompute,
        mix_classification_ids: mixClassificationsForCompute
          ? selectedClassificationIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [],
      });
      const timedRes = await API.fetchEventPlayers(event_id, {
        game_types: "TIMED",
        gender: gender === "all" ? undefined : gender,
      });
      const timedRows = timedRes?.data || timedRes || [];
      const timedList = Array.isArray(timedRows) ? timedRows : [];
      if (timedList.length) {
        setPlayers(timedList);
      } else {
        const allRes = await API.fetchEventPlayers(event_id, {
          gender: gender === "all" ? undefined : gender,
        });
        const allRows = allRes?.data || allRes || [];
        const allList = Array.isArray(allRows) ? allRows : [];
        setPlayers(allList.filter((r) => isTimedLike(r)));
      }
      setMessage("Positions computed successfully.");
    } catch (err) {
      setMessage(err?.message || "Failed to compute positions.");
    }
  };

  const exportReport = async (format) => {
    setExportingKey(format);
    try {
      await API.downloadResultsReport(event_id, "timed", format);
      setMessage(`${format.toUpperCase()} exported successfully!`);
    } catch (err) {
      setMessage(err?.message || `Error exporting ${format.toUpperCase()}`);
    } finally {
      setExportingKey("");
    }
  };

  const handleGenderChange = (nextGender) => {
    const normalized = ["all", "male", "female"].includes(nextGender) ? nextGender : "all";
    setGender(normalized);
    const q = new URLSearchParams(searchParams);
    q.set("gender", normalized);
    setSearchParams(q, { replace: true });
  };

  const toggleSort = (key, defaultDir = "asc") => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(defaultDir);
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const printResultsTable = () => {
    if (!sortedPlayers.length) {
      setMessage("No rows available to print.");
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const rowsHtml = sortedPlayers
      .map((row, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>${escapeHtml(row.name || "-")}</td>
          <td>${escapeHtml(row.gender || "-")}</td>
          <td>${escapeHtml(row.game_name || "-")}</td>
          <td>${escapeHtml(row.district || "-")}</td>
          <td>${escapeHtml(row.age_category || "-")}</td>
          <td>${escapeHtml(getClassificationDisplayName(row))}</td>
          <td>${escapeHtml(row.result_time || "")}</td>
          <td>${escapeHtml(row.points_score ?? "")}</td>
          <td>${escapeHtml(row.result_position ?? "")}</td>
        </tr>
      `)
      .join("");

    const selectedGameName =
      gameOptions.find(([id]) => String(id) === String(selectedGameId))?.[1] || "All Games";
    const isParaAthletics = isParaAthleticsSport(eventMeta?.sport_name);
    const eventTitle = eventMeta?.title || (isParaAthletics ? "TIMED RESULTS" : `Event #${event_id}`);
    const descriptionText =
      selectedGame?.game_narration ||
      eventMeta?.description ||
      "-";
    const eventDate = date || eventMeta?.date || "-";
    const venue = eventMeta?.venue || eventMeta?.location || "-";
    const leftMetaText = `${String(activeGenderLabel || "All").toUpperCase()} ${String(selectedGameName || "ALL GAMES").toUpperCase()}`.trim();

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Timed Results</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; color: #111827; }
          h1 { margin: 0 0 4px; font-size: 22px; text-align: center; font-weight: 800; }
          h2 { margin: 0 0 8px; font-size: 18px; text-align: center; font-weight: 700; }
          .meta { font-size: 12px; color: #374151; margin-bottom: 6px; text-align: center; }
          .meta strong { color: #111827; }
          .meta-wrap { margin-bottom: 12px; padding: 2px 0; }
          .meta-row { display: flex; justify-content: space-between; align-items: center; margin: 2px 0 8px; font-size: 12px; color: #1f2937; border-bottom: 1px solid #d1d5db; padding-bottom: 3px; gap: 10px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #d1d5db; padding: 5px; text-align: left; }
          th { background: #f3f4f6; }
          td:nth-child(1), td:nth-last-child(1), td:nth-last-child(2), td:nth-last-child(3) { text-align: center; }
        </style>
      </head>
      <body>
        <h1>PARASPORTS ASSOCIATION OF ANDHRA PRADESH</h1>
        <h2>${escapeHtml(eventTitle)}</h2>
        <div class="meta-wrap">
          <div class="meta"><strong>Description:</strong> ${escapeHtml(descriptionText)}</div>
          <div class="meta"><strong>Date:</strong> ${escapeHtml(eventDate)}</div>
          <div class="meta"><strong>Venue:</strong> ${escapeHtml(venue)}</div>
        </div>
        <div class="meta-row"><span>${escapeHtml(leftMetaText)}</span></div>
        <table>
          <thead>
            <tr>
              <th>S No</th>
              <th>Player</th>
              <th>Gender</th>
              <th>Game</th>
              <th>District</th>
              <th>Category</th>
              <th>Classification</th>
              <th>Time</th>
              <th>Points</th>
              <th>Rank</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
      </html>
    `;

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);
    const doc = frame.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(frame);
      setMessage("Unable to open print preview.");
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(frame);
      }, 250);
    }, 220);
  };

  const toggleTimer = () => {
    if (timerRunning) {
      const delta = Date.now() - timerStartRef.current;
      const nextBase = toNonNegativeMs(timerBaseMsRef.current + delta);
      timerBaseMsRef.current = nextBase;
      setLiveElapsedMs(nextBase);
      setTimerRunning(false);
      return;
    }
    timerStartRef.current = Date.now();
    setTimerRunning(true);
  };

  const resetTimer = () => {
    timerBaseMsRef.current = 0;
    setLiveElapsedMs(0);
    setTimerRunning(false);
  };

  const adjustLiveTime = (deltaMs) => {
    if (timerRunning) {
      const elapsedSinceStart = Date.now() - timerStartRef.current;
      const nextBase = toNonNegativeMs(timerBaseMsRef.current + elapsedSinceStart + deltaMs);
      timerBaseMsRef.current = nextBase;
      timerStartRef.current = Date.now();
      setLiveElapsedMs(nextBase);
      return;
    }
    const next = toNonNegativeMs(timerBaseMsRef.current + deltaMs);
    timerBaseMsRef.current = next;
    setLiveElapsedMs(next);
  };

  const loadSelectedIntoTimer = () => {
    if (!selectedCompetitor?.result_time) return;
    const ms = timeMaskToMs(formatTimeMask(selectedCompetitor.result_time));
    timerBaseMsRef.current = ms;
    setLiveElapsedMs(ms);
    setTimerRunning(false);
  };

  const applyTimerToSelected = () => {
    if (!selectedCompetitor) {
      setMessage("Select a competitor first.");
      return;
    }
    const masked = msToTimeMask(liveElapsedMs);
    updateField(
      selectedCompetitor.registration_id,
      selectedCompetitor.game_id,
      "result_time",
      masked
    );
    setMessage(`Applied ${masked} to ${selectedCompetitor.name || "selected competitor"}.`);
  };

  const timerDisplay = msToTimeMask(liveElapsedMs);
  const padButtonBase =
    "rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white select-none transition-all active:translate-y-[2px] focus:outline-none focus:ring-2 focus:ring-offset-2";
  const padBlue =
    "bg-gradient-to-b from-sky-500 to-blue-700 shadow-[0_5px_0_#1d4ed8,0_12px_24px_rgba(30,64,175,0.35)] hover:brightness-110 focus:ring-blue-400";
  const padEmerald =
    "bg-gradient-to-b from-emerald-400 to-emerald-700 shadow-[0_5px_0_#0f766e,0_12px_24px_rgba(15,118,110,0.35)] hover:brightness-110 focus:ring-emerald-400";
  const padAmber =
    "bg-gradient-to-b from-amber-400 to-amber-700 shadow-[0_5px_0_#b45309,0_12px_24px_rgba(180,83,9,0.35)] hover:brightness-110 focus:ring-amber-400";
  const padRose =
    "bg-gradient-to-b from-rose-400 to-rose-700 shadow-[0_5px_0_#be123c,0_12px_24px_rgba(190,24,93,0.35)] hover:brightness-110 focus:ring-rose-400";

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Timed Results</h1>
            <div className="flex gap-2 items-center">
              <label className="text-sm text-gray-700">
                Gender
                <select
                  value={gender}
                  onChange={(e) => handleGenderChange(e.target.value)}
                  className="ml-2 rounded border border-gray-300 px-2 py-1"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <button
                onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
                className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Back to Game Day
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-700 mb-3">Active Filter: Gender = {activeGenderLabel}</p>

          {message ? <div className="mb-3 text-sm bg-gray-100 p-3 rounded-lg">{message}</div> : null}

          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Game Mode</span>
            <button
              type="button"
              onClick={() => setGameMode("manual")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "manual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setGameMode("live")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "live" ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Live
            </button>
          </div>

          {gameMode === "live" ? (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-white shadow p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Live Timer Pad</h2>
              <div className="text-xs sm:text-sm text-slate-600">
                Selected: <span className="font-semibold text-slate-800">{selectedCompetitor?.name || "-"}</span>
              </div>
            </div>
            <div className="mt-3 grid lg:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-slate-900 text-emerald-300 p-4 font-mono text-4xl sm:text-5xl tracking-wider text-center shadow-inner">
                {timerDisplay}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={toggleTimer}
                  className={`${padButtonBase} ${timerRunning ? padRose : padEmerald}`}
                >
                  {timerRunning ? "Pause" : "Start"}
                </button>
                <button
                  type="button"
                  onClick={resetTimer}
                  className={`${padButtonBase} ${padAmber}`}
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={applyTimerToSelected}
                  disabled={!selectedCompetitor}
                  className={`${padButtonBase} ${padBlue} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Apply Time
                </button>
                <button type="button" onClick={() => adjustLiveTime(-10000)} className={`${padButtonBase} ${padBlue}`}>
                  -10s
                </button>
                <button type="button" onClick={() => adjustLiveTime(-1000)} className={`${padButtonBase} ${padBlue}`}>
                  -1s
                </button>
                <button type="button" onClick={() => adjustLiveTime(-100)} className={`${padButtonBase} ${padBlue}`}>
                  -100ms
                </button>
                <button type="button" onClick={() => adjustLiveTime(100)} className={`${padButtonBase} ${padBlue}`}>
                  +100ms
                </button>
                <button type="button" onClick={() => adjustLiveTime(1000)} className={`${padButtonBase} ${padBlue}`}>
                  +1s
                </button>
                <button type="button" onClick={() => adjustLiveTime(10000)} className={`${padButtonBase} ${padBlue}`}>
                  +10s
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadSelectedIntoTimer}
                disabled={!selectedCompetitor?.result_time}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-slate-50 text-slate-700 text-sm font-medium hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Load Selected Time
              </button>
              <div className="text-xs text-slate-500 self-center">
                Tip: click a row below to arm this pad for that competitor.
              </div>
            </div>
          </div>
          ) : null}

          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <div className="p-3 border-b bg-gray-50">
              <div className="flex gap-2 flex-wrap">
                <SearchableSelect
                  value={selectedGameId}
                  onChange={(e) => {
                    setSelectedGameId(e.target.value);
                    setSelectedClassificationIds([]);
                    setEditCombinedSelection(false);
                  }}
                  className="border p-2 rounded"
                >
                  <option value="">All Games</option>
                  {gameOptions.map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </SearchableSelect>
                <label className="flex items-center gap-2 text-sm px-2">
                  <input
                    type="checkbox"
                    checked={mixClassificationsForCompute}
                    onChange={(e) => {
                      setMixClassificationsForCompute(e.target.checked);
                      setEditCombinedSelection(false);
                    }}
                  />
                  Mix selected classifications for compute
                </label>
              </div>
              <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                {showCombinedAsSingleOption && combinedCategoryOption ? (
                  <label className="sm:col-span-2 lg:col-span-4 flex items-center gap-2 border rounded px-2 py-1 bg-white">
                    <input
                      type="checkbox"
                      checked={selectedClassificationIds.length > 0}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setSelectedClassificationIds([]);
                          setEditCombinedSelection(true);
                        }
                      }}
                    />
                    <span>{combinedCategoryOption.name} ({combinedCategoryOption.count})</span>
                  </label>
                ) : (
                  classificationOptions.map((c) => {
                    const checked = selectedClassificationIds.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedClassificationIds((prev) => {
                              if (e.target.checked) {
                                return Array.from(new Set([...prev, c.id]));
                              }
                              return prev.filter((x) => x !== c.id);
                            });
                          }}
                        />
                        <span>{c.name} ({c.count})</span>
                      </label>
                    );
                  })
                )}
              </div>
              {combinedCategoryOption ? (
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-600 truncate">
                    {combinedCategoryOption.details}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditCombinedSelection((prev) => !prev)}
                    className="text-xs text-blue-700 hover:text-blue-900 underline"
                  >
                    {showCombinedAsSingleOption ? "Edit Combined Selection" : "Show Single Combined Option"}
                  </button>
                </div>
              ) : null}
              {appliedGeneratedSheet ? (
                <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                  <span className="font-semibold">Applied from generated score sheet:</span>{" "}
                  {appliedGeneratedSheet.mode} | {appliedGeneratedSheet.gameName} | {appliedGeneratedSheet.categoryText}
                  {appliedGeneratedSheet.totalPlayers !== null ? ` | Total Players: ${appliedGeneratedSheet.totalPlayers}` : ""}
                  {appliedGeneratedSheet.updatedAt && appliedGeneratedSheet.updatedAt !== "-"
                    ? ` | Updated: ${appliedGeneratedSheet.updatedAt}`
                    : ""}
                </div>
              ) : null}
              <p className="mt-2 text-xs text-slate-600">
                Showing {sortedPlayers.length} of {players.length} players.
              </p>
            </div>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th>S No</th>
                  <th>
                    <button type="button" onClick={() => toggleSort("name", "asc")} className="font-semibold">
                      Player{sortIcon("name")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("gender", "asc")} className="font-semibold">
                      Gender{sortIcon("gender")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("game", "asc")} className="font-semibold">
                      Game{sortIcon("game")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("district", "asc")} className="font-semibold">
                      District{sortIcon("district")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("category", "asc")} className="font-semibold">
                      Category{sortIcon("category")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("classification", "asc")} className="font-semibold">
                      Classification{sortIcon("classification")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("time", "asc")} className="font-semibold">
                      Time{sortIcon("time")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("points", "desc")} className="font-semibold">
                      Points{sortIcon("points")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("rank", "asc")} className="font-semibold">
                      Position{sortIcon("rank")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((p, i) => (
                  <tr
                    key={`${p.registration_id}-${p.game_id}-${i}`}
                    className={
                      rowKey(p) === selectedCompetitorKey
                        ? "bg-emerald-50 ring-1 ring-emerald-200 cursor-pointer"
                        : "hover:bg-slate-50 cursor-pointer"
                    }
                    onClick={() => setSelectedCompetitorKey(rowKey(p))}
                  >
                    <td>{i + 1}</td>
                    <td>{p.name}</td>
                    <td>{p.gender || "-"}</td>
                    <td>{p.game_name}</td>
                    <td>{p.district}</td>
                    <td>{p.age_category || "-"}</td>
                    <td>{getClassificationDisplayName(p)}</td>
                    <td>
                      <input
                        type="text"
                        value={p.result_time || ""}
                        onChange={(e) => updateField(p.registration_id, p.game_id, "result_time", e.target.value)}
                        placeholder="mm:ss:ms"
                        className="w-full rounded-md border border-slate-300 px-2 py-1"
                      />
                    </td>
                    <td className="text-center">
                      {p.points_score !== null && p.points_score !== undefined && p.points_score !== ""
                        ? p.points_score
                        : "-"}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={p.result_position || ""}
                        onChange={(e) => updateField(p.registration_id, p.game_id, "result_position", e.target.value)}
                        className="w-full rounded-md border border-slate-300 px-2 py-1"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={saveAll} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              Save Results
            </button>
            <button onClick={computeResults} className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">
              Compute Results
            </button>
            <button
              onClick={printResultsTable}
              className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800"
            >
              Print
            </button>
            <button
              onClick={() => exportReport("pdf")}
              disabled={exportingKey === "pdf"}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {exportingKey === "pdf" ? "Exporting..." : "Download PDF"}
            </button>
            <button
              onClick={() => exportReport("excel")}
              disabled={exportingKey === "excel"}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:bg-gray-400"
            >
              {exportingKey === "excel" ? "Exporting..." : "Download Excel"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimedResults;
